/*
 * hp-variant-transition — makes a variant change read as one event.
 *
 * ── THE PROBLEM IS NOT "IT IS ABRUPT", IT IS THAT IT HAPPENS TWICE ────────
 * Measured on gloopy-ashtray-by-seth, clicking a colour:
 *
 *     23ms   gallery and buy row mutate      (local, no network)
 *    314ms   payment terms mutate            \
 *    314ms   accordions mutate                >  the Section Rendering fetch
 *    315ms   price mutates                   /
 *
 * So the page changes, then changes AGAIN a third of a second later. The
 * gallery answers the click immediately, which is right; the price, the
 * instalment line and the accordion copy then snap in long after the eye has
 * settled, with nothing in between to say they were coming. Two events where
 * the shopper performed one.
 *
 * There is no loading state anywhere in product-info.js to lean on —
 * `renderProductInfo` fetches and calls back with nothing in between.
 *
 * ── WHAT THIS DOES ───────────────────────────────────────────────────────
 * Mutes exactly the regions that arrive LATE, from the moment of the click,
 * and lets them fade back when the new markup lands. The gallery is left
 * alone deliberately: it is the fast half, and dimming the one thing that
 * already responded would make the page feel slower, not smoother.
 *
 * ── THREE THINGS THAT DECIDE WHETHER IT HELPS OR HURTS ───────────────────
 *
 * 1. IT HANGS ON THE INTERACTION, NOT THE REQUEST. CLAUDE.md records the cart
 *    learning this the expensive way: a state set when the fetch starts
 *    appears after the debounce and the shopper is no longer looking at the
 *    thing they pressed. This listens for `change` on the picker, undebounced,
 *    so the mute is on screen in the same frame as the selection.
 *
 * 2. A FAST SWAP MUST SHOW NOTHING AT ALL. A 120ms dim-and-restore is a
 *    flicker, and a flicker reads as a bug. The mute is therefore delayed in
 *    CSS (`transition-delay`), so a swap that completes before the delay
 *    elapses never becomes visible — the class goes on and comes off having
 *    painted nothing. Only a wait long enough to notice is ever shown.
 *
 * 3. IT MUST NOT BE ABLE TO STICK. `handleUpdateProductInfo` returns EARLY for
 *    an unavailable variant — `setUnavailable()` then `return`, before
 *    `publish(variantChange)` — so the completion event this listens for never
 *    arrives on exactly the combination a shopper is most likely to click by
 *    accident. A permanently dimmed buy column would be far worse than the
 *    abruptness this exists to fix, so a timeout clears it regardless.
 */
(function () {
  const SECTION = '.hp-pdp';
  const BUSY_CLASS = 'hp-variant-busy';

  /* Long enough to cover a slow section fetch, short enough that a failure
     recovers while the shopper is still on the page. The measured update lands
     at ~315ms; this is an order of magnitude of headroom. */
  const FAILSAFE_MS = 4000;

  function start(scope) {
    scope.classList.add(BUSY_CLASS);
    clearTimeout(scope.__hpVariantTimer);
    scope.__hpVariantTimer = setTimeout(function () {
      scope.classList.remove(BUSY_CLASS);
    }, FAILSAFE_MS);
  }

  function end(scope) {
    clearTimeout(scope.__hpVariantTimer);
    scope.classList.remove(BUSY_CLASS);
  }

  /*
   * Delegated on the document rather than bound to the inputs: the variant
   * picker is itself replaced by `updateOptionValues` on every change, so
   * anything bound to an input is bound to an element that will not exist
   * after the first swatch click.
   */
  document.addEventListener('change', function (event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('variant-selects')) return;
    const scope = target.closest(SECTION);
    if (scope) start(scope);
  });

  /*
   * The picker's swatches are labels over radios, and a click on the label of
   * an ALREADY-selected option fires no `change` — correctly, since nothing
   * changed. Nothing to clear in that case either, so only a real change is
   * handled and the no-op stays a no-op.
   */

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, function () {
      document.querySelectorAll(SECTION + '.' + BUSY_CLASS).forEach(end);
    });
  }
})();
